import { useEffect, useState } from "react";
import { useParams, Link, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import {
  FileText,
  Calculator,
  Phone,
  MapPin,
  Gauge,
  Fuel,
  Cog,
  Palette,
  Sparkles,
  Headset,
  ChevronLeft,
  ChevronRight,
  Loader2,
  BadgeCheck,
  Lock,
  Tag,
} from "lucide-react";
import { api, priceLabel } from "@/lib/api";
import { brandStyle, BRAND } from "@/lib/brand";
import { getUnlock } from "@/lib/unlock";
import { BrandLogo } from "@/components/BrandLogo";
import { VinconexLogo, PoweredBy } from "@/components/VinconexMark";
import { QuickLeadForm } from "@/components/LeadCapture";
import LeadWallDialog from "@/components/LeadWallDialog";
import { AsIsBadge } from "@/components/AsIsBadge";
import { InsiderDetails } from "@/components/InsiderDetails";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import SpecialistMatcher from "@/components/SpecialistMatcher";
import SpecialistRequestDialog from "@/components/SpecialistRequestDialog";

function Spec({ icon: Icon, label, value }) {
  if (!value) return null;
  return (
    <div className="flex items-center gap-3 rounded-lg border border-[#2A2E3D] bg-[#12141C] px-4 py-3">
      <Icon className="h-4 w-4 text-brand shrink-0" />
      <div className="min-w-0">
        <div className="text-[10px] uppercase tracking-widest text-slate-500 font-mono-tech">{label}</div>
        <div className="text-sm text-slate-200 truncate">{value}</div>
      </div>
    </div>
  );
}

export default function VehiclePublic() {
  const { vehicleId } = useParams();
  const [searchParams] = useSearchParams();
  const [data, setData] = useState(null);
  const [state, setState] = useState("loading");
  const [idx, setIdx] = useState(0);
  const [matcherOpen, setMatcherOpen] = useState(false);
  const [wall, setWall] = useState(null);          // lower_price | carfax | payment (open dialog)
  const [unlocked, setUnlocked] = useState(null);  // what this shopper already unlocked (1-hour token)

  // Re-check the shopper's unlock (saved after any lead form) so walls stay open for an hour across scans
  const refreshUnlock = (dealerId, lead) => {
    const saved = getUnlock(dealerId);
    const token = lead?.unlock_token || saved?.token;
    if (!token) return;
    api.get(`/public/vehicles/${vehicleId}/unlocked`, { params: { token } }).then(({ data }) => setUnlocked(data)).catch(() => setUnlocked(null));
  };

  useEffect(() => {
    api
      .get(`/public/vehicles/${vehicleId}`, { params: { tour: searchParams.get("tour") || undefined } })
      .then(({ data }) => {
        if (data.archived) {
          // The unit left the dealer site: keep counting the sticker scan, then send the shopper to the Vinconex home page.
          const v = data.vehicle || {};
          const label = [v.year, v.make, v.model].filter(Boolean).join(" ");
          const src = searchParams.get("qr") === "1" ? "qr" : "web";
          const go = () => window.location.replace(`/?sold=${encodeURIComponent(data.dealer?.slug || "")}&vehicle=${encodeURIComponent(label)}`);
          api.post(`/public/vehicles/${vehicleId}/scan`, { source: src }).then(go, go);
          return;
        }
        setData(data);
        setState("ok");
        refreshUnlock(data.dealer?.id);
        const fromQr = searchParams.get("qr") === "1";
        const key = `vx-seen-${vehicleId}-${fromQr ? "qr" : "web"}`;
        if (!sessionStorage.getItem(key)) {
          sessionStorage.setItem(key, "1");
          api.post(`/public/vehicles/${vehicleId}/scan`, { source: fromQr ? "qr" : "web" }).catch(() => {});
        }
      })
      .catch(() => setState("notfound"));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [vehicleId]);

  if (state === "loading") {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#0A0B0E]">
        <Loader2 className="h-8 w-8 animate-spin text-brand" />
      </div>
    );
  }

  if (state === "notfound") {
    return (
      <div className="min-h-screen bg-[#0A0B0E] flex flex-col items-center justify-center text-center px-6">
        <VinconexLogo variant="full" className="h-28 w-auto" />
        <h1 className="font-heading text-3xl font-black text-white mt-8">This vehicle is no longer available</h1>
        <p className="text-slate-400 mt-3 max-w-md">
          It may have just sold. Head back to the dealership's live inventory to see what's on the lot.
        </p>
        <Link to="/" className="mt-6">
          <Button className="bg-[#0A8CF2] hover:bg-[#3AA3FF] text-white font-bold">Back to Vinconex</Button>
        </Link>
      </div>
    );
  }

  const { vehicle: v, dealer } = data;
  const matchEnabled = !!dealer.features?.specialist_match;
  const gallery = v.images && v.images.length ? v.images : v.image ? [v.image] : [];
  const walls = dealer.walls || {};
  const isOpen = !!unlocked?.unlocked;
  const carfaxUrl = v.carfax_url || unlocked?.carfax_url;
  const carfaxWalled = walls.carfax && v.has_carfax && !carfaxUrl;
  const carfaxRequest = v.condition === "Used" && !v.has_carfax;   // no working report link yet → shopper can ask for it
  const paymentUrl = dealer.payment_calculator_url || unlocked?.payment_url || v.source_url;
  const paymentWalled = walls.payment && v.has_payment && !paymentUrl;
  const lowerWalled = walls.lower_price && v.has_lower_price && v.price != null;
  const lowerPrice = isOpen && unlocked.lower_price != null ? unlocked.lower_price : null;
  const estimateWalled = walls.estimate && v.has_estimate && !isOpen;
  const wallBtn = "w-full h-11 font-semibold border-dashed";

  return (
    <div className="min-h-screen bg-[#0A0B0E] text-white grid-noise" style={brandStyle(dealer.accent_color)}>
      {/* Nav */}
      <header className="sticky top-0 z-40 backdrop-blur-xl bg-black/70 border-b border-[#2A2E3D]">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
          <Link to={dealer.slug ? `/d/${dealer.slug}` : "/"} data-testid="dealer-home-link">
            <BrandLogo dealer={dealer} />
          </Link>
          {dealer.phone && (
            <a
              href={`tel:${dealer.phone}`}
              data-testid="dealer-phone-link"
              className="flex items-center gap-2 text-sm text-slate-300 hover:text-white"
            >
              <Phone className="h-4 w-4 text-brand" />
              <span className="hidden sm:inline">{dealer.phone}</span>
            </a>
          )}
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 sm:px-6 py-6 sm:py-10">
        <div className="flex items-center gap-2 mb-4">
          <Badge className="bg-brand text-white hover:bg-brand font-semibold">{v.condition}</Badge>
          {v.as_is && (
            <AsIsBadge vehicle={v} unlocked={isOpen ? unlocked : null} walled={estimateWalled} onWall={() => setWall("estimate")} />
          )}
          {v.mileage != null && (
            <Badge variant="outline" className="border-slate-700 text-slate-300">
              {v.mileage.toLocaleString()} mi
            </Badge>
          )}
          <span className="ml-auto text-[10px] font-mono-tech uppercase tracking-widest text-slate-500 flex items-center gap-1">
            <Sparkles className="h-3 w-3 text-brand" /> Scanned from vehicle
          </span>
        </div>

        <div className="grid lg:grid-cols-12 gap-6">
          {/* Gallery */}
          <motion.div
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            className="lg:col-span-8"
          >
            <div className="relative rounded-2xl overflow-hidden border border-[#2A2E3D] bg-black aspect-[16/10]">
              {gallery.length > 0 ? (
                <img
                  src={gallery[idx]}
                  alt={v.name}
                  data-testid="vehicle-hero-image"
                  className="w-full h-full object-cover"
                />
              ) : (
                <div className="w-full h-full flex items-center justify-center text-slate-600">No image</div>
              )}
              {gallery.length > 1 && (
                <>
                  <button
                    onClick={() => setIdx((i) => (i - 1 + gallery.length) % gallery.length)}
                    className="absolute left-3 top-1/2 -translate-y-1/2 h-10 w-10 rounded-full bg-black/60 backdrop-blur flex items-center justify-center border border-white/10 hover:bg-brand"
                    data-testid="gallery-prev"
                  >
                    <ChevronLeft className="h-5 w-5" />
                  </button>
                  <button
                    onClick={() => setIdx((i) => (i + 1) % gallery.length)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 h-10 w-10 rounded-full bg-black/60 backdrop-blur flex items-center justify-center border border-white/10 hover:bg-brand"
                    data-testid="gallery-next"
                  >
                    <ChevronRight className="h-5 w-5" />
                  </button>
                  <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex gap-1.5">
                    {gallery.map((_, i) => (
                      <span
                        key={i}
                        className={`h-1.5 rounded-full transition-all ${
                          i === idx ? "w-6 bg-brand" : "w-1.5 bg-white/40"
                        }`}
                      />
                    ))}
                  </div>
                </>
              )}
            </div>

            {/* Specs */}
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3 mt-4">
              <Spec icon={Gauge} label="Mileage" value={v.mileage != null ? `${v.mileage.toLocaleString()} mi` : null} />
              <Spec icon={Cog} label="Drivetrain" value={v.drivetrain} />
              <Spec icon={Cog} label="Transmission" value={v.transmission} />
              <Spec icon={Fuel} label="Fuel" value={v.fuel_type} />
              <Spec icon={Palette} label="Exterior" value={v.exterior_color} />
              <Spec icon={Palette} label="Interior" value={v.interior_color} />
              <Spec icon={Cog} label="Engine" value={v.engine} />
              <Spec icon={Fuel} label="MPG" value={v.mpg ? `${v.mpg} mpg` : null} />
              <Spec icon={BadgeCheck} label="Body" value={v.body_type} />
            </div>

            {/* Features */}
            {v.features && v.features.length > 0 && (
              <div className="mt-6 rounded-2xl border border-[#2A2E3D] bg-[#12141C] p-5">
                <h3 className="font-heading text-lg font-bold mb-3">Features & options</h3>
                <div className="flex flex-wrap gap-2">
                  {v.features.map((f) => (
                    <span
                      key={f}
                      className="text-xs px-3 py-1.5 rounded-full bg-slate-900 border border-slate-800 text-slate-300"
                    >
                      {f}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {v.description && (
              <p className="mt-5 text-sm text-slate-400 leading-relaxed">{v.description}</p>
            )}
          </motion.div>

          {/* Sticky action rail */}
          <motion.div
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="lg:col-span-4"
          >
            <div className="lg:sticky lg:top-24 space-y-4">
              <div className="tracing-top rounded-2xl border border-[#2A2E3D] bg-[#12141C] p-6">
                <h1
                  data-testid="qr-scan-hero-vehicle-name"
                  className="font-heading text-2xl font-black leading-tight"
                >
                  {v.year} {v.make} {v.model}
                </h1>
                <p className="text-slate-400 text-sm mt-1">{v.trim}</p>

                {lowerPrice != null ? (
                  <div className="mt-4" data-testid="vehicle-unlocked-price">
                    <div className="text-[10px] uppercase tracking-widest text-emerald-400 font-mono-tech flex items-center gap-1"><Tag className="h-3 w-3" /> Your unlocked price</div>
                    <div className="flex items-baseline gap-3 flex-wrap">
                      <span data-testid="vehicle-price-tag" className="font-heading text-4xl font-black text-brand">{priceLabel(lowerPrice)}</span>
                      {unlocked.savings > 0 && (
                        <span className="text-sm text-slate-400"><s>{priceLabel(v.price)}</s> · save <b className="text-emerald-400">{priceLabel(unlocked.savings)}</b></span>
                      )}
                    </div>
                  </div>
                ) : (
                  <div className="mt-4">
                    <div className="flex items-baseline gap-2">
                      <span data-testid="vehicle-price-tag" className="font-heading text-4xl font-black text-brand">
                        {priceLabel(v.price)}
                      </span>
                    </div>
                    {lowerWalled && (
                      <button
                        onClick={() => setWall("lower_price")}
                        data-testid="unlock-lower-price-btn"
                        className="mt-3 w-full flex items-center justify-between gap-3 rounded-xl border border-dashed border-emerald-500/50 bg-emerald-500/10 px-4 py-3 text-left hover:bg-emerald-500/20 transition-colors"
                      >
                        <span className="flex items-center gap-2 text-sm font-semibold text-emerald-300"><Lock className="h-4 w-4" /> Unlock a lower price</span>
                        <span className="text-[11px] text-slate-400">Leave your details</span>
                      </button>
                    )}
                  </div>
                )}

                <div className="mt-3 font-mono-tech text-xs uppercase tracking-widest text-slate-500 space-y-1">
                  {v.vin && <div>VIN · <span className="text-red-400">{v.vin}</span></div>}
                  {v.stock_number && <div>Stock · {v.stock_number}</div>}
                </div>

                <div className="mt-6 space-y-3">
                  {matchEnabled ? (
                    <Button
                      onClick={() => setMatcherOpen(true)}
                      data-testid="match-specialist-btn"
                      className="w-full h-12 bg-brand hover:bg-brand/90 text-white font-bold text-base shadow-lg shadow-brand/30 transition-all hover:-translate-y-0.5"
                    >
                      <Sparkles className="mr-2 h-4 w-4" /> Match me with a specialist
                    </Button>
                  ) : (
                    <Button
                      onClick={() => setMatcherOpen(true)}
                      data-testid="talk-to-specialist-btn"
                      className="w-full h-12 bg-brand hover:bg-brand/90 text-white font-bold text-base shadow-lg shadow-brand/30 transition-all hover:-translate-y-0.5"
                    >
                      <Headset className="mr-2 h-4 w-4" /> Talk to a specialist
                    </Button>
                  )}

                  {carfaxWalled ? (
                    <Button
                      onClick={() => setWall("carfax")}
                      data-testid="carfax-wall-button"
                      variant="outline"
                      className={`${wallBtn} border-[#3777BC]/60 bg-[#3777BC]/10 hover:bg-[#3777BC]/20 text-[#7FB4EE]`}
                    >
                      <Lock className="mr-2 h-4 w-4" /> View CARFAX Report
                    </Button>
                  ) : carfaxUrl ? (
                    <Button
                      asChild
                      data-testid="carfax-report-button"
                      variant="outline"
                      className="w-full h-11 border-[#3777BC]/60 bg-[#3777BC]/10 hover:bg-[#3777BC]/20 text-[#7FB4EE] font-semibold"
                    >
                      <a href={carfaxUrl} target="_blank" rel="noopener"><FileText className="mr-2 h-4 w-4" /> View CARFAX Report</a>
                    </Button>
                  ) : carfaxRequest && (
                    <Button
                      onClick={() => setWall("carfax_request")}
                      data-testid="carfax-request-button"
                      variant="outline"
                      className={`${wallBtn} border-[#3777BC]/60 bg-[#3777BC]/10 hover:bg-[#3777BC]/20 text-[#7FB4EE]`}
                    >
                      <FileText className="mr-2 h-4 w-4" /> Request CARFAX Report
                    </Button>
                  )}

                  {paymentWalled ? (
                    <Button
                      onClick={() => setWall("payment")}
                      data-testid="payment-wall-button"
                      variant="outline"
                      className={`${wallBtn} border-slate-600 bg-slate-900/50 hover:bg-slate-800 text-slate-200`}
                    >
                      <Lock className="mr-2 h-4 w-4" /> Payment Calculator
                    </Button>
                  ) : paymentUrl && (
                    <a href={paymentUrl} target="_blank" rel="noreferrer" className="block">
                      <Button
                        data-testid="payment-calculator-button"
                        variant="outline"
                        className="w-full h-11 border-slate-700 bg-slate-900/50 hover:bg-slate-800 text-slate-200 font-semibold"
                      >
                        <Calculator className="mr-2 h-4 w-4" /> Payment Calculator
                      </Button>
                    </a>
                  )}

                  {dealer.slug && (
                    <Link to={`/d/${dealer.slug}/new-arrivals`} className="block">
                      <Button
                        data-testid="new-arrivals-cta"
                        variant="outline"
                        className="w-full h-11 border-brand/40 bg-brand/10 hover:bg-brand/20 text-brand-soft font-semibold"
                      >
                        <Sparkles className="mr-2 h-4 w-4" /> See what just arrived
                      </Button>
                    </Link>
                  )}
                </div>
              </div>

              <QuickLeadForm dealer={dealer} vehicle={v} onLead={(lead) => refreshUnlock(dealer.id, lead)} />

              <InsiderDetails vehicle={v} dealer={dealer} unlocked={isOpen ? unlocked : null} onWall={setWall} />

              <div className="rounded-2xl border border-[#2A2E3D] bg-[#12141C] p-5">
                <div className="flex items-center gap-2 text-sm font-semibold mb-1">
                  <MapPin className="h-4 w-4 text-brand" /> {dealer.name}
                </div>
                {dealer.address && <p className="text-xs text-slate-400">{dealer.address}</p>}
                {dealer.phone && (
                  <a href={`tel:${dealer.phone}`} className="text-xs text-brand-soft mt-2 inline-block">
                    {dealer.phone}
                  </a>
                )}
                {dealer.slug && (
                  <Link to={`/d/${dealer.slug}`} data-testid="browse-inventory-link" className="block text-xs text-slate-400 hover:text-white mt-3 underline underline-offset-2">
                    Browse all {dealer.name} inventory
                  </Link>
                )}
              </div>
            </div>
          </motion.div>
        </div>
      </main>

      <footer className="border-t border-[#2A2E3D] py-6 mt-6">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 flex flex-col sm:flex-row items-center justify-between gap-3">
          <span className="text-xs text-slate-500">{dealer.name}{dealer.address ? ` · ${dealer.address}` : ""}</span>
          <div className="flex items-center gap-3">
            <PoweredBy />
            <a href="/privacy" data-testid="footer-privacy-link" className="text-[10px] text-slate-500 hover:text-white">Privacy</a>
            <span className="text-[10px] text-slate-600" data-testid="owner-line">© {new Date().getFullYear()} {BRAND.owner}</span>
          </div>
        </div>
      </footer>

      {matchEnabled ? (
        <SpecialistMatcher
          open={matcherOpen}
          onOpenChange={setMatcherOpen}
          vehicle={v}
          dealer={dealer}
        />
      ) : (
        <SpecialistRequestDialog open={matcherOpen} onOpenChange={setMatcherOpen} vehicle={v} dealer={dealer} onLead={(lead) => refreshUnlock(dealer.id, lead)} />
      )}
      <LeadWallDialog open={!!wall} onOpenChange={(o) => !o && setWall(null)} kind={wall || "lower_price"} vehicle={v} dealer={dealer} onUnlocked={setUnlocked} />
    </div>
  );
}
